/** A person named by an identifier elsewhere in the product — **the one place that decides how**.
 *
 * Records name people as UUIDs (rule 4): a risk's owner, a task's owner, a ledger entry's actor.
 * What a *screen* does with that identifier depends on who is reading and on what the screen is for,
 * and this component is that decision so two screens cannot make it differently:
 *
 * - **An Admin may follow it.** The People roster is Admin-only, so the link to `/people/$userId` is
 *   rendered for an Admin and for nobody else. A link a Contributor could click is a page that
 *   predictably refuses them (design rule 6).
 * - **A name is shown only where the screen is allowed to show one.** `showName` is true on every
 *   screen since 2026-11-03, the audit ledger included: an Admin sees what the People screen already
 *   shows them, read from the same query, so this adds no request, and the identifier is on hover.
 *   The chain itself still holds identifiers only (rule 4); only this rendering resolves one.
 * - **No name is ever invented.** Not on the roster (a person of another workspace, or one the
 *   roster no longer lists) reads as the identifier, with the reason beside it, not as "unknown".
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useMe } from "../api/auth";
import { usePersonLabels } from "../api/people";
import { useMembers } from "../api/queries";
import { keepSearch } from "./search";
import own from "./PersonRef.module.css";

/** A person's name, for a screen that must not print an identifier where a reader wants a name (U7).
 *
 * **Who is told what is the server's rule, not this file's** (ADR-0218 option C, D-15). The company
 * Admin reads the roster (`GET /members`, already on their screens, so no extra request). Every
 * other role asks `GET /people/names`, which answers with a name for the company's own roles, the
 * **role** for an Auditor (never a name), "Erased member" for an erased person, and nothing for
 * anyone who is not a member of this workspace. This returns the string to print, or `undefined`
 * (still being read, not answered, or the read failed) for the screen's own words.
 *
 * The identifier stays the fact: it is in the element's `title` where one is drawn, and the ledger's
 * payloads still hold identifiers only (rule 4); the name is resolved at read time and kept nowhere.
 */
export function useNameOf(): (userId: string | null | undefined) => string | undefined {
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const roster = useMembers(isAdmin);
  const labelOf = usePersonLabels();
  return (userId) => {
    if (!userId) return undefined;
    if (isAdmin) return roster.data?.find((entry) => entry.user_id === userId)?.display_name;
    return labelOf(userId)?.text;
  };
}

/** What an Admin reads where a name should be and the roster could not be read (G-P9-21). The
 * identifier is **not** the answer: an Admin's screen prints names, and a column of UUIDs because one
 * request failed reads as the product having changed. The failure itself is said once, above the
 * screen (`shell/LookupsFailed`), with its Try again; the identifier is on hover. */
export const NAME_NOT_LOADED = "Name not loaded";

/** What a non-Admin reads where a colleague should be named and the server does not answer for them
 * (a person who is not a member of this workspace, or a read still in flight): never the identifier. */
export const A_COLLEAGUE = "A colleague";

/** `useNameOf`, drawn: the name with the identifier on hover, else `fallback`. */
export function PersonName({
  userId,
  fallback,
}: {
  userId: string | null | undefined;
  fallback: ReactNode;
}) {
  const nameOf = useNameOf();
  const { data: me } = useMe();
  const roster = useMembers(me?.role === "admin");
  const name = nameOf(userId);
  if (userId && name === undefined && roster.error) {
    return (
      <span title={userId} data-testid="person-name-unread" data-person-id={userId}>
        {NAME_NOT_LOADED}
      </span>
    );
  }
  if (!userId || name === undefined) return <>{fallback}</>;
  return (
    <span title={userId} data-testid="person-name" data-person-id={userId}>
      {name}
    </span>
  );
}

export function PersonRef({ userId, showName }: { userId: string; showName: boolean }) {
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const roster = useMembers(isAdmin && showName);
  const nameOf = useNameOf();
  const member = roster.data?.find((entry) => entry.user_id === userId);
  // A roster that could not be read leaves the identifier unexplained: say the name did not load
  // (the notice above the screen says why), keeping the identifier on hover. A person the roster
  // *did* answer without (another workspace's) keeps the identifier, which is the honest answer.
  const unread = showName && !member && Boolean(roster.error);
  const label = showName && member ? member.display_name : unread ? NAME_NOT_LOADED : userId;

  if (!isAdmin) {
    // The server's answer, never the identifier: a name for the company's own roles, the role for an
    // Auditor. A person it does not answer for is "A colleague" (ADR-0218).
    return (
      <span className={own.name} data-testid="person-ref" data-person-id={userId}>
        {nameOf(userId) ?? A_COLLEAGUE}
      </span>
    );
  }
  return (
    <Link
      className={showName && member ? own.name : own.id}
      to="/people/$userId"
      params={{ userId }}
      search={keepSearch}
      title={userId}
      data-testid="person-ref"
      data-person-id={userId}
    >
      {label}
    </Link>
  );
}
